import React, { useEffect } from 'react';
import { Modal, Spin, Empty, Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { executeCustomAnalytics } from '@/redux/rtk/features/CRM/analytics/analyticsSlice';
import BarChartComponent from '../Charts/BarChartComponent';
import PieChartComponent from '../Charts/PieChartComponent';
import { DownloadOutlined, FileTextOutlined, FileImageOutlined } from '@ant-design/icons';
import { exportToCSV, exportElementAsImage } from '@/utils/exportUtils';
import { Dropdown } from 'antd';

const ViewAnalyticsModal = ({ visible, onClose, analytic, onDrillDown }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { customAnalytics, loadingCustom } = useSelector((state) => state.analytics);

    useEffect(() => {
        if (visible && analytic) {
            let parsedFilters = analytic.filters || {};
            if (typeof parsedFilters === 'string') {
                try {
                    parsedFilters = JSON.parse(parsedFilters);
                } catch (e) {
                    parsedFilters = {};
                }
            }

            const config = {
                dataSource: analytic.dataSource,
                filters: parsedFilters,
                groupBy: analytic.chartConfig?.groupBy,
                aggregation: analytic.chartConfig?.aggregation,
                timeRange: analytic.chartConfig?.timePeriod,
            };
            dispatch(executeCustomAnalytics(config));
        }
    }, [visible, analytic, dispatch]);
    
    const handleChartClick = (data) => {
        if (!onDrillDown || !analytic) return;

        const label = data.activeLabel || data.name;
        if (!label) return;

        // Find the item in customAnalytics that matches this label to get its records
        const clickedItem = customAnalytics?.find(item => item.label === label);
        const records = clickedItem?.records;

        const dataSource = analytic.dataSource?.toLowerCase();
        let type = "leads";
        if (dataSource === 'deals') type = "deals";
        if (dataSource === 'applications') type = "applications";

        const groupBy = analytic.chartConfig?.groupBy;
        const drillFilters = {};

        // Try to map groupBy to a filter field
        if (groupBy === 'dealStageId') drillFilters.dealStageId = label;
        else if (groupBy === 'leadSourceId') drillFilters.leadSourceId = label;
        else if (groupBy === 'dealOwnerId' || groupBy === 'leadOwnerId') drillFilters.dealOwnerId = label;
        else if (groupBy === 'nationality') drillFilters.nationality = label;
        else if (groupBy === 'country') drillFilters.country = label;
        else if (groupBy === 'degree') drillFilters.degree = label;
        else {
            // Fallback to search key
            drillFilters.key = label;
        }

        onDrillDown(`${analytic.name} - ${label}`, type, drillFilters, records);
        onClose(); // Optional: close the custom analytics view to show the records
    };
    
    const chartContainerId = `custom-chart-container-${analytic?.id}`;

    const items = [
        {
            key: 'csv',
            label: t('common.exportCSV') || 'CSV',
            icon: <FileTextOutlined />,
            onClick: () => exportToCSV(customAnalytics, analytic?.name || 'custom_analytics')
        },
        {
            key: 'image',
            label: t('common.exportImage') || 'Image',
            icon: <FileImageOutlined />,
            onClick: () => exportElementAsImage(chartContainerId, analytic?.name || 'chart')
        }
    ];

    const renderChart = () => {
        if (loadingCustom) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                    <Spin size="large" />
                </div>
            );
        }

        if (!customAnalytics || customAnalytics.length === 0) {
            return <Empty description={t('analytics.noDataFound')} />;
        }

        // Prepare data for charts
        // Custom analytics returns array of { label: '...', value: ... } which matches what charts expect mostly
        // BUT BarChartComponent expects `xAxisKey` and `yAxisKey`
        // PieChartComponent expects `dataKey` and `nameKey`

        const commonProps = {
            data: customAnalytics,
            title: analytic?.name,
            loading: loadingCustom,
            onClick: handleChartClick
        };

        // Backend QueryBuilderEngine now standardizes output to `label` and `value`
        // So we can use these keys directly regardless of the data source
        const xAxisKey = 'label';
        const yAxisKey = 'value';

        switch (analytic?.chartType) {
            case 'pie':
                return (
                    <PieChartComponent
                        {...commonProps}
                        dataKey={yAxisKey}
                        nameKey={xAxisKey}
                        allowExport={false}
                    />
                );
            case 'bar':
            default:
                return (
                    <BarChartComponent
                        {...commonProps}
                        xAxisKey={xAxisKey}
                        yAxisKey={yAxisKey}
                        color="#3b82f6"
                        allowExport={false}
                    />
                );
        }
    };

    return (
        <Modal
            title={analytic?.name || t('analytics.viewAnalytics')}
            open={visible}
            onCancel={onClose}
            footer={[
                <Dropdown key="export-dropdown" menu={{ items }} placement="topRight" disabled={!customAnalytics || customAnalytics.length === 0}>
                    <Button 
                        icon={<DownloadOutlined />}
                    >
                        {t('common.export')}
                    </Button>
                </Dropdown>,
                <Button key="close" onClick={onClose}>
                    {t('common.close')}
                </Button>
            ]}
            width={800}
        >
            <div id={chartContainerId} style={{ minHeight: '400px', background: '#fff', padding: '20px' }}>
                {analytic?.description && (
                    <p style={{ color: '#666', marginBottom: '20px' }}>{analytic.description}</p>
                )}
                {renderChart()}
            </div>
        </Modal>
    );
};

export default ViewAnalyticsModal;
